import axios from 'axios';
import type { OfferAction } from './types';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Attach auth token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('ats_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && localStorage.getItem('ats_token')) {
      localStorage.removeItem('ats_token');
      localStorage.removeItem('ats_user');
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

// === Auth ===
export const login = (username: string, password: string) =>
  api.post('/auth/login', { username, password }).then(r => r.data);
export const register = (username: string, password: string, display_name?: string, invite_code?: string) =>
  api.post('/auth/register', { username, password, display_name, invite_code }).then(r => r.data);
export const getMe = () => api.get('/auth/me').then(r => r.data);
export const logout = () => api.post('/auth/logout').then(r => r.data);
export const hasUsers = () => api.get('/auth/has-users').then(r => r.data);
export const getInviteInfo = (code: string) => api.get(`/auth/invite/${code}`).then(r => r.data);
export const createInviteLink = (data: { role?: string; position_ids?: string[] }) =>
  api.post('/auth/invite', data).then(r => r.data);
export const getFeishuConfig = () => api.get('/auth/feishu-config').then(r => r.data);
export const feishuLogin = (code: string, redirect_uri?: string) =>
  api.post('/auth/feishu-login', { code, redirect_uri }).then(r => r.data);

// === Users ===
export const getUsers = () => api.get('/users').then(r => r.data);
export const getMentionableUsers = () => api.get('/users/mentionable').then(r => r.data);
export const inviteUser = (data: { username: string; password: string; display_name?: string; role?: string; position_ids?: string[] }) =>
  api.post('/users/invite', data).then(r => r.data);
export const updateUser = (id: string, data: any) =>
  api.put(`/users/${id}`, data).then(r => r.data);
export const updateUserPositions = (id: string, position_ids: string[]) =>
  api.put(`/users/${id}/positions`, { position_ids }).then(r => r.data);
export const resetUserPassword = (id: string, password: string) =>
  api.put(`/users/${id}/password`, { password }).then(r => r.data);
export const deleteUser = (id: string) =>
  api.delete(`/users/${id}`).then(r => r.data);

// === Permissions ===
export const getPermissionModules = () => api.get('/permissions/modules').then(r => r.data);
export const getRolePermissions = () => api.get('/permissions/roles').then(r => r.data);
export const updateRolePermissions = (role: string, data: { modules?: string[]; position_scope?: 'all' | 'specific'; notify_events?: string[]; approval_scopes?: string[] }) =>
  api.put(`/permissions/roles/${role}`, data).then(r => r.data);
export const getUserPermissions = (id: string) => api.get(`/permissions/users/${id}`).then(r => r.data);
export const updateUserPermissions = (id: string, data: { modules?: string[] | null; position_scope?: 'all' | 'specific' | null; notify_events?: string[] | null; approval_scopes?: string[] | null }) =>
  api.put(`/permissions/users/${id}`, data).then(r => r.data);
export const resetUserPermissions = (id: string) =>
  api.delete(`/permissions/users/${id}`).then(r => r.data);

// === Positions ===
export const getPositions = () => api.get('/positions').then(r => r.data);
export const getPosition = (id: string) => api.get(`/positions/${id}`).then(r => r.data);
export const createPosition = (data: Record<string, any>) =>
  api.post('/positions', data).then(r => r.data);
export const updatePosition = (id: string, data: any) =>
  api.put(`/positions/${id}`, data).then(r => r.data);
export const deletePosition = (id: string) =>
  api.delete(`/positions/${id}`).then(r => r.data);
// 岗位生命周期：draft → open（写 published_at）/ open → paused（释放可释放的编制）
export const publishPosition = (id: string) =>
  api.post(`/positions/${id}/publish`).then(r => r.data);
export const pausePosition = (id: string) =>
  api.post(`/positions/${id}/pause`).then(r => r.data);
// 暂停/关闭前的编制释放预览，供二次确认弹窗；与实际执行共用后端同一份判定
export const getPositionReleasePreview = (id: string) =>
  api.get(`/positions/${id}/release-preview`).then(r => r.data);
// 批量关闭：先取预览展示影响面，再执行；执行结果分 closed / skipped / failed 三类
export const getBatchReleasePreview = (positionIds: string[]) =>
  api.post('/positions/batch-release-preview', { position_ids: positionIds }).then(r => r.data);
export const batchClosePositions = (positionIds: string[]) =>
  api.post('/positions/batch-close', { position_ids: positionIds }).then(r => r.data);

// === 岗位↔编制绑定 ===
export const getPositionBindings = (positionId: string, includeHistory?: boolean) =>
  api.get(`/positions/${positionId}/bindings`, { params: includeHistory ? { include_history: '1' } : undefined }).then(r => r.data);
// 后端已按「同部门 + AVAILABLE + 无 active 绑定」收敛，前端不再过滤
export const getBindableSlots = (positionId: string) =>
  api.get(`/positions/${positionId}/bindable-slots`).then(r => r.data);
export const bindSlotToPosition = (positionId: string, slotId: string) =>
  api.post(`/positions/${positionId}/bindings`, { slot_id: slotId }).then(r => r.data);
export const unbindSlotFromPosition = (positionId: string, slotId: string, reason?: string) =>
  api.delete(`/positions/${positionId}/bindings/${slotId}`, { params: reason ? { reason } : undefined }).then(r => r.data);

// === Offer（编制锁定链路） ===
export const getOffers = (params?: { candidate_id?: string; position_id?: string; headcount_slot_id?: string; status?: string }) =>
  api.get('/offers', { params }).then(r => r.data);
export const getOffer = (id: string) => api.get(`/offers/${id}`).then(r => r.data);
/** 预览某编制将生成的审批链（业务VP→HRD→CEO+CFO），供发起表单展示/覆盖 */
export const getOfferApprovalPreview = (headcount_slot_id: string) =>
  api.get('/offers/approval-preview', { params: { headcount_slot_id } }).then(r => r.data as { approval_chain: import('./types').OfferApprovalNode[] });
/** 发起 Offer：支持附带方案截图(offer_image)与指定审批人(approver_user_ids)，走 multipart */
export const createOffer = (data: {
  candidate_id: string; headcount_slot_id: string; salary_annual?: number;
  employment_type?: string; expected_onboard_date?: string; probation_months?: number;
  work_location?: string; notes?: string; reason?: string;
  comp_amount?: number; comp_unit?: string; comp_currency?: string; comp_pretax?: boolean;
  intern_end_date?: string; accept_deadline?: string; perks?: string;
  contract_end_date?: string; probation_end_date?: string; probation_salary?: number;
  year_end_multiplier?: string; equity_value?: number;
  full_attendance_bonus?: number; post_allowance?: number; transport_allowance?: number;
  skill_allowance_pre?: number; skill_allowance_post?: number; management_allowance?: number;
  monthly_performance?: number; quarterly_performance?: number; half_year_performance?: number;
  sign_on_bonus?: number; has_noncompete?: boolean; other_notes?: string;
  approver_user_ids?: string[]; offer_image?: File | null;
  assessment_file?: File | null; background_check_file?: File | null; noncompete_file?: File | null;
  approval_overrides?: Record<string, { user_id: string; name?: string }>;
}) => {
  const fd = new FormData();
  fd.append('candidate_id', data.candidate_id);
  fd.append('headcount_slot_id', data.headcount_slot_id);
  if (data.salary_annual != null) fd.append('salary_annual', String(data.salary_annual));
  if (data.employment_type) fd.append('employment_type', data.employment_type);
  if (data.expected_onboard_date) fd.append('expected_onboard_date', data.expected_onboard_date);
  if (data.probation_months != null) fd.append('probation_months', String(data.probation_months));
  if (data.work_location) fd.append('work_location', data.work_location);
  if (data.notes) fd.append('notes', data.notes);
  if (data.reason) fd.append('reason', data.reason);
  if (data.comp_amount != null) fd.append('comp_amount', String(data.comp_amount));
  if (data.comp_unit) fd.append('comp_unit', data.comp_unit);
  if (data.comp_currency) fd.append('comp_currency', data.comp_currency);
  if (data.comp_pretax != null) fd.append('comp_pretax', data.comp_pretax ? '1' : '0');
  if (data.intern_end_date) fd.append('intern_end_date', data.intern_end_date);
  if (data.accept_deadline) fd.append('accept_deadline', data.accept_deadline);
  if (data.perks) fd.append('perks', data.perks);
  if (data.contract_end_date) fd.append('contract_end_date', data.contract_end_date);
  if (data.probation_end_date) fd.append('probation_end_date', data.probation_end_date);
  if (data.probation_salary != null) fd.append('probation_salary', String(data.probation_salary));
  if (data.year_end_multiplier) fd.append('year_end_multiplier', data.year_end_multiplier);
  if (data.equity_value != null) fd.append('equity_value', String(data.equity_value));
  if (data.full_attendance_bonus != null) fd.append('full_attendance_bonus', String(data.full_attendance_bonus));
  if (data.post_allowance != null) fd.append('post_allowance', String(data.post_allowance));
  if (data.transport_allowance != null) fd.append('transport_allowance', String(data.transport_allowance));
  if (data.skill_allowance_pre != null) fd.append('skill_allowance_pre', String(data.skill_allowance_pre));
  if (data.skill_allowance_post != null) fd.append('skill_allowance_post', String(data.skill_allowance_post));
  if (data.management_allowance != null) fd.append('management_allowance', String(data.management_allowance));
  if (data.monthly_performance != null) fd.append('monthly_performance', String(data.monthly_performance));
  if (data.quarterly_performance != null) fd.append('quarterly_performance', String(data.quarterly_performance));
  if (data.half_year_performance != null) fd.append('half_year_performance', String(data.half_year_performance));
  if (data.sign_on_bonus != null) fd.append('sign_on_bonus', String(data.sign_on_bonus));
  if (data.has_noncompete != null) fd.append('has_noncompete', data.has_noncompete ? '1' : '0');
  if (data.other_notes) fd.append('other_notes', data.other_notes);
  fd.append('approver_user_ids', JSON.stringify(data.approver_user_ids || []));
  if (data.approval_overrides) fd.append('approval_overrides', JSON.stringify(data.approval_overrides));
  if (data.offer_image) fd.append('offer_image', data.offer_image);
  if (data.assessment_file) fd.append('assessment_file', data.assessment_file);
  if (data.background_check_file) fd.append('background_check_file', data.background_check_file);
  if (data.noncompete_file) fd.append('noncompete_file', data.noncompete_file);
  return api.post('/offers', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
/** 状态流转：approve/reject/send/accept/decline/withdraw/complete，编制锁的获取与释放由后端处理 */
export const transitionOffer = (id: string, action: OfferAction, reason?: string) =>
  api.post(`/offers/${id}/${action}`, { reason }).then(r => r.data);
/** 上传 Offer PDF 附件 */
export const uploadOfferAttachmentPdf = (id: string, file: File) => {
  const fd = new FormData();
  fd.append('offer_pdf', file);
  return api.post(`/offers/${id}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
/** 上传 Offer 方案截图 */
export const uploadOfferAttachmentImage = (id: string, file: File) => {
  const fd = new FormData();
  fd.append('offer_image', file);
  return api.post(`/offers/${id}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

// === Candidates ===
export const getCandidates = (params?: Record<string, string>) =>
  api.get('/candidates', { params }).then(r => r.data);
export const getCandidate = (id: string) =>
  api.get(`/candidates/${id}`).then(r => r.data);
export const getResumeSnapshot = (candidateId: string, snapshotId: string) =>
  api.get(`/candidates/${candidateId}/resume-snapshots/${snapshotId}`).then(r => r.data);
export const createCandidate = (data: any) =>
  api.post('/candidates', data).then(r => r.data);
export const updateCandidate = (id: string, data: any) =>
  api.put(`/candidates/${id}`, data).then(r => r.data);
export const deleteCandidate = (id: string) =>
  api.delete(`/candidates/${id}`).then(r => r.data);
export const updateCandidateStage = (id: string, stage: string) =>
  api.patch(`/candidates/${id}/stage`, { stage }).then(r => r.data);
export const addCandidateTag = (candidateId: string, tagId: string) =>
  api.post(`/candidates/${candidateId}/tags`, { tag_id: tagId }).then(r => r.data);
export const removeCandidateTag = (candidateId: string, tagId: string) =>
  api.delete(`/candidates/${candidateId}/tags/${tagId}`).then(r => r.data);
export const getPipelineStats = (positionId?: string) =>
  api.get('/candidates/stats/pipeline', { params: positionId ? { position_id: positionId } : {} }).then(r => r.data);
export const releaseCandidate = (id: string) =>
  api.patch(`/candidates/${id}/release`).then(r => r.data);
export const assignCandidate = (id: string, positionId: string) =>
  api.patch(`/candidates/${id}/assign`, { position_id: positionId }).then(r => r.data);
export const batchAssignCandidates = (candidateIds: string[], positionId: string) =>
  api.patch('/candidates/batch-assign', { candidate_ids: candidateIds, position_id: positionId }).then(r => r.data);

// === Tags ===
export const getTags = () => api.get('/tags').then(r => r.data);
export const createTag = (data: { name: string; color?: string }) =>
  api.post('/tags', data).then(r => r.data);
export const deleteTag = (id: string) =>
  api.delete(`/tags/${id}`).then(r => r.data);

// === Evaluations ===
export const getEvaluations = (candidateId: string) =>
  api.get(`/evaluations/candidate/${candidateId}`).then(r => r.data);
export const createEvaluation = (data: any) =>
  api.post('/evaluations', data).then(r => r.data);
export const updateEvaluation = (id: string, data: any) =>
  api.put(`/evaluations/${id}`, data).then(r => r.data);
export const deleteEvaluation = (id: string) =>
  api.delete(`/evaluations/${id}`).then(r => r.data);
export const getEvaluationHistory = (id: string) =>
  api.get(`/evaluations/${id}/history`).then(r => r.data);

// === Upload ===
export const uploadResume = (file: File) => {
  const formData = new FormData();
  formData.append('resume', file);
  return api.post('/upload/resume', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const uploadPhoto = (file: File) => {
  const formData = new FormData();
  formData.append('photo', file);
  return api.post('/upload/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// === Parse Resume (AI) ===
export const parseResumeSingle = (file: File) => {
  const formData = new FormData();
  formData.append('resume', file);
  formData.append('originalName', file.name);
  return api.post('/parse-resume/single', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then(r => r.data);
};

export const parseResumeBatch = (files: File[]) => {
  const formData = new FormData();
  files.forEach(f => formData.append('resumes', f));
  formData.append('originalNames', JSON.stringify(files.map(f => f.name)));
  return api.post('/parse-resume/batch', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  }).then(r => r.data);
};

// === Records (Screening / Interview / Offer) ===
export const getScreeningRecords = (candidateId: string) =>
  api.get(`/records/screening/${candidateId}`).then(r => r.data);
export const createScreeningRecord = (data: { candidate_id: string; result: string; comment: string }) =>
  api.post('/records/screening', data).then(r => r.data);

export const getInterviewRecords = (candidateId: string) =>
  api.get(`/records/interviews/${candidateId}`).then(r => r.data);
export const createInterviewRecord = (data: { candidate_id: string; interviewer_ids: string[]; round: string; scheduled_time: string; end_time?: string; duration_minutes?: number; location?: string; interview_type?: string; meeting_room_id?: string }) =>
  api.post('/records/interviews', data).then(r => r.data);
export const updateInterviewRecord = (id: string, data: any) =>
  api.put(`/records/interviews/${id}`, data).then(r => r.data);
export const addInterviewInterviewer = (interviewId: string, userId: string) =>
  api.post(`/records/interviews/${interviewId}/interviewers`, { user_id: userId }).then(r => r.data);
export const removeInterviewInterviewer = (interviewId: string, userId: string) =>
  api.delete(`/records/interviews/${interviewId}/interviewers/${userId}`).then(r => r.data);
export const nudgeInterviewEvaluation = (interviewId: string, interviewerIds?: string[]) =>
  api.post(`/records/interviews/${interviewId}/nudge`, interviewerIds?.length ? { interviewer_ids: interviewerIds } : {}).then(r => r.data);

// === Salary Materials ===
export const getSalaryMaterials = (candidateId: string) =>
  api.get(`/records/salary-materials/${candidateId}`).then(r => r.data);
export const uploadSalaryMaterials = (candidateId: string, files: File[], note?: string) => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  // Pass UTF-8 original names explicitly to avoid multer latin1 encoding issues
  formData.append('originalNames', JSON.stringify(files.map(f => f.name)));
  if (note) formData.append('note', note);
  return api.post(`/records/salary-materials/${candidateId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const deleteSalaryMaterial = (id: string) =>
  api.delete(`/records/salary-materials/${id}`).then(r => r.data);

export const getOfferApprovals = (candidateId: string) =>
  api.get(`/records/offers/${candidateId}`).then(r => r.data);
export const getAllOfferApprovals = () =>
  api.get('/records/offers-all').then(r => r.data);
export const getEvalSummary = (candidateId: string) =>
  api.get(`/records/eval-summary/${candidateId}`).then(r => r.data);
export const submitOfferApproval = (formData: FormData) =>
  api.post('/records/offers', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
export const approveOffer = (id: string, data: { status: string; comment: string }) =>
  api.put(`/records/offers/${id}/approve`, data).then(r => r.data);
export const withdrawOffer = (id: string, data: { reason: string }) =>
  api.put(`/records/offers/${id}/withdraw`, data).then(r => r.data);
export const nudgeOffer = (id: string) =>
  api.post(`/records/offers/${id}/nudge`).then(r => r.data);
export const uploadOfferPdf = (id: string, file: File) => {
  const formData = new FormData();
  formData.append('offer_pdf', file);
  return api.post(`/records/offers/${id}/upload-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// === Onboarding ===
export const getOnboardingRecords = () =>
  api.get('/onboarding').then(r => r.data);
export const getOnboardingRecord = (candidateId: string) =>
  api.get(`/onboarding/${candidateId}`).then(r => r.data);
export const saveOnboardingRecord = (data: any) =>
  api.post('/onboarding', data).then(r => r.data);
export const updateOnboardingStatus = (id: string, status: string) =>
  api.put(`/onboarding/${id}/status`, { status }).then(r => r.data);

// === IT资源工单 ===
export const getItResourceRequests = () =>
  api.get('/it-resources').then(r => r.data);
export const createItResourceRequest = (data: { candidate_id: string; notes?: string; expected_onboard_date?: string; urgency?: string }) =>
  api.post('/it-resources', data).then(r => r.data);
export const updateItResourceRequestStatus = (id: string, status: string) =>
  api.put(`/it-resources/${id}/status`, { status }).then(r => r.data);
export const updateItResourceRequest = (id: string, data: { urgency?: string; notes?: string; expected_onboard_date?: string }) =>
  api.put(`/it-resources/${id}`, data).then(r => r.data);
export const nudgeItResourceRequest = (id: string) =>
  api.post(`/it-resources/${id}/nudge`).then(r => r.data);

// === 导师分配 ===
export const assignMentor = (candidateId: string, mentor_id: string) =>
  api.post(`/onboarding/${candidateId}/assign-mentor`, { mentor_id }).then(r => r.data);
export const submitMentorPrep = (candidateId: string, notes: string) =>
  api.post(`/onboarding/${candidateId}/mentor-prep`, { notes }).then(r => r.data);

// === Org Mapping ===
export const getOrgOverview = () =>
  api.get('/org/overview').then(r => r.data);
export const getOrgBlueprint = () =>
  api.get('/org/blueprint').then(r => r.data);
export const getOrgMatrix = () =>
  api.get('/org/matrix').then(r => r.data);
export const getOrgChanges = () =>
  api.get('/org/changes').then(r => r.data);
export const createOrgUnit = (data: any) =>
  api.post('/org/units', data).then(r => r.data);
export const updateOrgUnit = (id: string, data: any) =>
  api.put(`/org/units/${id}`, data).then(r => r.data);
export const moveOrgUnit = (id: string, data: { parent_id?: string | null; sort_order?: number }) =>
  api.post(`/org/units/${id}/move`, data).then(r => r.data);
export const reorderOrgUnits = (data: { parent_id?: string | null; ordered_ids: string[] }) =>
  api.post('/org/units/reorder', data).then(r => r.data);
export const archiveOrgUnit = (id: string) =>
  api.post(`/org/units/${id}/archive`).then(r => r.data);
export const createOrgMember = (data: any) =>
  api.post('/org/members', data).then(r => r.data);
export const updateOrgMember = (id: string, data: any) =>
  api.put(`/org/members/${id}`, data).then(r => r.data);
export const createOrgResponsibility = (data: any) =>
  api.post('/org/responsibilities', data).then(r => r.data);
export const updateOrgResponsibility = (id: string, data: any) =>
  api.put(`/org/responsibilities/${id}`, data).then(r => r.data);
export const createOrgAssignment = (data: any) =>
  api.post('/org/assignments', data).then(r => r.data);
export const updateOrgAssignment = (id: string, data: any) =>
  api.put(`/org/assignments/${id}`, data).then(r => r.data);

// === AI Search ===
export const aiSearch = (query: string) =>
  api.post('/ai/search', { query }).then(r => r.data);
export const generateShareLink = (candidateId: string) =>
  api.post(`/share/candidate/${candidateId}`).then(r => r.data);
export const aiAnalyzeResume = (candidateId: string, autoUpdate?: boolean, positionJd?: string) =>
  api.post('/ai/analyze-resume', { candidate_id: candidateId, auto_update: autoUpdate, position_jd: positionJd }).then(r => r.data);
export const aiGenerateEvaluation = (candidateId: string, transcript: string, round?: string) =>
  api.post('/ai/generate-evaluation', { candidate_id: candidateId, transcript, round }).then(r => r.data);
export const aiScoreFeedback = (data: { candidate_id: string; position_id?: string; ai_score?: number; hr_verdict: 'agree' | 'too_high' | 'too_low'; reason?: string }) =>
  api.post('/ai/score-feedback', data).then(r => r.data);
export const getScoreFeedbackSummary = (positionId?: string) =>
  api.get('/ai/score-feedback/summary', { params: { position_id: positionId } }).then(r => r.data);

// === Feishu ===
export const bindFeishu = (data: { mobile?: string; email?: string }) =>
  api.post('/feishu/bind', data).then(r => r.data);
export const unbindFeishu = () =>
  api.post('/feishu/unbind').then(r => r.data);
export const bindFeishuForUser = (userId: string, data: { mobile?: string; email?: string }) =>
  api.post(`/feishu/bind/${userId}`, data).then(r => r.data);
export const unbindFeishuForUser = (userId: string) =>
  api.post(`/feishu/unbind/${userId}`).then(r => r.data);
export const notifyReview = (data: { candidate_ids: string[]; interviewer_ids: string[] }) =>
  api.post('/feishu/notify-review', data).then(r => r.data);
export const shareCandidateToFeishu = (candidate_id: string, user_ids: string[]) =>
  api.post('/feishu/share-candidate', { candidate_id, user_ids }).then(r => r.data);
export const getReviewTasks = (candidateId?: string) =>
  api.get('/feishu/review-tasks', { params: candidateId ? { candidate_id: candidateId } : {} }).then(r => r.data);
export const getFreeBusy = (userId: string, start: string, end: string) =>
  api.get('/feishu/freebusy', { params: { user_id: userId, start, end } }).then(r => r.data);
export const rescheduleInterview = (id: string, data: any) =>
  api.put(`/records/interviews/${id}`, data).then(r => r.data);
export const cancelInterview = (id: string) =>
  api.delete(`/records/interviews/${id}`).then(r => r.data);
export const getMeetingRooms = () =>
  api.get('/records/meeting-rooms').then(r => r.data);
export const getFeishuMeetingRooms = () =>
  api.get('/feishu/meeting-rooms').then(r => r.data);
export const getRoomFreebusy = (start: string, end: string) =>
  api.get('/feishu/room-freebusy', { params: { start, end } }).then(r => r.data);
export const createMeetingRoom = (data: { name: string; capacity?: number; location?: string }) =>
  api.post('/records/meeting-rooms', data).then(r => r.data);

// === Backup ===
export const getBackupStats = () => api.get('/backup/stats').then(r => r.data);
export const downloadBackup = () =>
  api.get('/backup/export', { responseType: 'blob' }).then(r => r.data);

// === Batch Import ===
export const batchImportResumes = (files: File[], positionId: string) => {
  const formData = new FormData();
  files.forEach(f => formData.append('resumes', f));
  formData.append('originalNames', JSON.stringify(files.map(f => f.name)));
  formData.append('position_id', positionId);
  return api.post('/candidates/batch-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }).then(r => r.data);
};

// === 需求发起 ===
export const getNeeds = () => api.get('/needs').then(r => r.data);
export const getNeed = (id: string) => api.get(`/needs/${id}`).then(r => r.data);
export const createNeed = (data: { department: string; business_problem: string; headcount: number; urgency: string; member_ids: string[]; need_source?: string }) =>
  api.post('/needs', data).then(r => r.data);
export const updateNeedMembers = (id: string, member_ids: string[]) =>
  api.put(`/needs/${id}/members`, { member_ids }).then(r => r.data);
export const addNeedComment = (id: string, data: { content: string; mentioned_user_ids?: string[] }) =>
  api.post(`/needs/${id}/comments`, data).then(r => r.data);
export const updateNeedJd = (id: string, data: { jd_title?: string; jd_responsibilities?: string; jd_requirements?: string; first_interviewer_ids?: string[]; second_interviewer_ids?: string[]; need_source?: string }) =>
  api.put(`/needs/${id}/jd`, data).then(r => r.data);
export const confirmNeed = (id: string) =>
  api.post(`/needs/${id}/confirm`).then(r => r.data);
export const deleteNeed = (id: string) =>
  api.delete(`/needs/${id}`).then(r => r.data);
export const getNeedDebateStatus = (id: string) =>
  api.get(`/needs/${id}/debate-status`).then(r => r.data);
export const redebateNeed = (id: string) =>
  api.post(`/needs/${id}/redebate`).then(r => r.data);

// === 编制管理 (Headcount) ===
export const getHeadcountOverview = (params?: { year?: string; department?: string }) =>
  api.get('/headcount/overview', { params }).then(r => r.data);
export const getHeadcountTrend = (params?: { granularity?: string; year?: string }) =>
  api.get('/headcount/trend', { params }).then(r => r.data);
export const getHeadcountSlots = (params?: { year?: string; department?: string; status?: string; hc_type?: string; search?: string; created_from?: string; created_to?: string }) =>
  api.get('/headcount/slots', { params }).then(r => r.data);
export const createHeadcountSlot = (data: any) =>
  api.post('/headcount/slots', data).then(r => r.data);
export const updateHeadcountSlot = (id: string, data: any) =>
  api.put(`/headcount/slots/${id}`, data).then(r => r.data);
// 状态流转由后端状态机校验，非法跳转返回 400（如 AVAILABLE→RESERVED 须先绑定并发起 Offer）
export const updateHeadcountSlotStatus = (id: string, status: string, reason?: string) =>
  api.put(`/headcount/slots/${id}/status`, { status, reason }).then(r => r.data);
// 释放 = 回到 AVAILABLE（重新可被绑定）；彻底收回请用 updateHeadcountSlotStatus(id, 'CLOSED')
export const releaseHeadcountSlot = (id: string, reason?: string) =>
  api.post(`/headcount/slots/${id}/release`, { reason }).then(r => r.data);
export const deleteHeadcountSlot = (id: string) =>
  api.delete(`/headcount/slots/${id}`).then(r => r.data);
export const importHeadcountSlots = (slots: any[]) =>
  api.post('/headcount/slots/import', { slots }).then(r => r.data);
export const getHeadcountRequests = (params?: { status?: string }) =>
  api.get('/headcount/requests', { params }).then(r => r.data);
export const getHeadcountRequestById = (id: string) =>
  api.get(`/headcount/requests/${id}`).then(r => r.data);
export const getHeadcountApproverCandidates = () =>
  api.get('/headcount/approver-candidates').then(r => r.data);
export const getHeadcountInterviewerCandidates = () =>
  api.get('/headcount/interviewer-candidates').then(r => r.data);
export const createHeadcountRequest = (data: any) =>
  api.post('/headcount/requests', data).then(r => r.data);
export const approveHeadcountRequest = (id: string, comment?: string) =>
  api.post(`/headcount/requests/${id}/approve`, { comment }).then(r => r.data);
export const rejectHeadcountRequest = (id: string, comment?: string) =>
  api.post(`/headcount/requests/${id}/reject`, { comment }).then(r => r.data);
export const cancelHeadcountRequest = (id: string) =>
  api.post(`/headcount/requests/${id}/cancel`).then(r => r.data);
export const getHeadcountChanges = (limit?: number) =>
  api.get('/headcount/changes', { params: { limit } }).then(r => r.data);
export const cleanupHeadcountData = (keep_position?: string) =>
  api.post('/headcount/cleanup', { keep_position }).then(r => r.data);
export const regenerateHeadcountCodes = () =>
  api.post('/headcount/regenerate-codes').then(r => r.data);

// === 消息通知 ===
export const getNotifications = (params?: { unread?: boolean; limit?: number; offset?: number }) =>
  api.get('/notifications', { params: { unread: params?.unread ? 'true' : undefined, limit: params?.limit, offset: params?.offset } }).then(r => r.data);
export const markNotificationRead = (id: string) =>
  api.put(`/notifications/${id}/read`).then(r => r.data);
export const markAllNotificationsRead = () =>
  api.put('/notifications/read-all').then(r => r.data);

// === 定时提醒 ===
export const getReminders = (candidateId: string) =>
  api.get('/reminders', { params: { candidate_id: candidateId } }).then(r => r.data);
export const createReminder = (data: { candidate_id: string; target_user_id: string; remind_at: string; content: string }) =>
  api.post('/reminders', data).then(r => r.data);
export const deleteReminder = (id: string) =>
  api.delete(`/reminders/${id}`).then(r => r.data);

// === Dashboard ===
export const getDashboard = () => api.get('/dashboard').then(r => r.data);
export const getDashboardAnalytics = (params?: { range?: string; position_id?: string }) =>
  api.get('/dashboard/analytics', { params }).then(r => r.data);
export const getInterviewSchedule = (start: string, end: string) =>
  api.get('/dashboard/interviews', { params: { start, end } }).then(r => r.data);
export const batchReview = (task_ids: string[], result: 'passed' | 'rejected') =>
  api.post('/dashboard/batch-review', { task_ids, result }).then(r => r.data);

// === 应聘材料 ===
export const getApplicationMaterials = (candidateId: string) =>
  api.get(`/application-materials/${candidateId}`).then(r => r.data);
export const createApplicationMaterial = (formData: FormData) =>
  api.post('/application-materials', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
export const deleteApplicationMaterial = (id: string) =>
  api.delete(`/application-materials/${id}`).then(r => r.data);

// === Boss简报 & 筛选规则 ===
export const getScreeningBrief = (days?: number) =>
  api.get('/screening/brief', { params: { days } }).then(r => r.data);
export const getScreeningRules = () =>
  api.get('/screening/rules').then(r => r.data);
export const createScreeningRule = (data: any) =>
  api.post('/screening/rules', data).then(r => r.data);
export const updateScreeningRule = (id: string, data: any) =>
  api.put(`/screening/rules/${id}`, data).then(r => r.data);
export const deleteScreeningRule = (id: string) =>
  api.delete(`/screening/rules/${id}`).then(r => r.data);
export const promoteCandidate = (id: string) =>
  api.post(`/screening/promote/${id}`).then(r => r.data);
export const rejectScreening = (id: string) =>
  api.post(`/screening/reject/${id}`).then(r => r.data);
export const confirmStaged = (candidate_ids: string[]) =>
  api.post('/screening/confirm-staged', { candidate_ids }).then(r => r.data);
export const dismissStaged = (candidate_ids: string[]) =>
  api.post('/screening/dismiss-staged', { candidate_ids }).then(r => r.data);
export const getScreeningDefaults = () =>
  api.get('/screening/defaults').then(r => r.data);
export const getApiKeys = () =>
  api.get('/screening/api-keys').then(r => r.data);
export const createApiKey = (data: { name?: string; expires_days?: number }) =>
  api.post('/screening/api-keys', data).then(r => r.data);
export const deleteApiKey = (id: string) =>
  api.delete(`/screening/api-keys/${id}`).then(r => r.data);

// === Talent Hub ===
export const getTalentTree = () =>
  api.get('/talent-hub/tree').then(r => r.data);
export const createEmployee = (data: any) =>
  api.post('/talent-hub/employees', data).then(r => r.data);
export const importEmployees = (rows: any[]) =>
  api.post('/talent-hub/employees/import', { rows }).then(r => r.data);
export const updateEmployee = (id: string, data: any) =>
  api.put(`/talent-hub/employees/${id}`, data).then(r => r.data);
export const deleteEmployee = (id: string) =>
  api.delete(`/talent-hub/employees/${id}`).then(r => r.data);
export const transferEmployee = (id: string, targetOrgUnitId: string) =>
  api.post(`/talent-hub/employees/${id}/transfer`, { target_org_unit_id: targetOrgUnitId }).then(r => r.data);
export const searchEmployees = (q: string) =>
  api.get('/talent-hub/employees/search', { params: { q } }).then(r => r.data);
export const getEmployeeTags = () =>
  api.get('/talent-hub/tags').then(r => r.data);
export const createEmployeeTag = (data: any) =>
  api.post('/talent-hub/tags', data).then(r => r.data);
export const addEmployeeTag = (empId: string, tagId: string) =>
  api.post(`/talent-hub/employees/${empId}/tags`, { tag_id: tagId }).then(r => r.data);
export const removeEmployeeTag = (empId: string, tagId: string) =>
  api.delete(`/talent-hub/employees/${empId}/tags/${tagId}`).then(r => r.data);
export const createTalentUnit = (data: any) =>
  api.post('/talent-hub/units', data).then(r => r.data);
export const updateTalentUnit = (id: string, data: any) =>
  api.put(`/talent-hub/units/${id}`, data).then(r => r.data);
export const moveTalentUnit = (id: string, data: any) =>
  api.post(`/talent-hub/units/${id}/move`, data).then(r => r.data);
export const archiveTalentUnit = (id: string) =>
  api.post(`/talent-hub/units/${id}/archive`).then(r => r.data);
export const getTalentRisks = () =>
  api.get('/talent-hub/risks').then(r => r.data);
export const getTalentSnapshots = () =>
  api.get('/talent-hub/snapshots').then(r => r.data);
export const createTalentSnapshot = (name: string) =>
  api.post('/talent-hub/snapshots', { name }).then(r => r.data);
export const getTalentChanges = () =>
  api.get('/talent-hub/changes').then(r => r.data);
export const verifyTalentHubPassword = (password: string) =>
  api.get('/talent-hub/password', { params: { password } }).then(r => r.data);
export const updateTalentHubPassword = (password: string) =>
  api.put('/talent-hub/password', { password }).then(r => r.data);

// === eHR 花名册（飞书通讯录同步，与人才中枢隔离）===
export const getEhrTree = () =>
  api.get('/ehr/tree').then(r => r.data);
export const getEhrSyncStatus = () =>
  api.get('/ehr/sync-status').then(r => r.data);
export const syncFeishuRoster = () =>
  api.post('/ehr/sync-feishu', {}, { timeout: 180000 }).then(r => r.data);
export const archiveNonFeishuDepts = () =>
  api.post('/ehr/archive-non-feishu').then(r => r.data);

// === HRG 数仓（已弃用，保留向后兼容）===
export const getHrgRoster = () =>
  api.get('/hrg/roster').then(r => r.data);
export const getHrgStatus = () =>
  api.get('/hrg/status').then(r => r.data);
export const syncHrgAll = () =>
  api.post('/hrg/sync/all').then(r => r.data);

// === Referral ===
export const getMyReferralCode = () =>
  api.get('/referral/my-code').then(r => r.data);
export const getMyReferrals = () =>
  api.get('/referral/my-referrals').then(r => r.data);
export const getReferralLeaderboard = () =>
  api.get('/referral/leaderboard').then(r => r.data);
export const getReferralDashboard = () =>
  api.get('/referral/dashboard').then(r => r.data);

// === 面试官行为规范 ===
export const getInterviewConduct = () =>
  api.get('/admin/settings/interview-conduct').then(r => r.data);
export const saveInterviewConduct = (content: string) =>
  api.post('/admin/settings', { INTERVIEW_CODE_OF_CONDUCT: content }).then(r => r.data);

// === Offer 录用通知书模版（多类型：intern / formal_no_equity / formal_equity）===
export type OfferTemplateType = 'intern' | 'formal_no_equity' | 'formal_equity';
export interface OfferTemplateResp {
  type: OfferTemplateType;
  name: string;
  settingsKey: string;
  content: string;
  placeholders: { key: string; label: string; sample: string }[];
  types: { key: OfferTemplateType; name: string }[];
}
export const getOfferTemplate = (type: OfferTemplateType = 'intern') =>
  api.get('/admin/settings/offer-template', { params: { type } }).then(r => r.data as OfferTemplateResp);
export const saveOfferTemplate = (content: string, settingsKey = 'OFFER_LETTER_TEMPLATE') =>
  api.post('/admin/settings', { [settingsKey]: content }).then(r => r.data);

// === 官网投递 ===
export const getWebsiteApplicants = (params?: { page?: number; pageSize?: number; search?: string }) =>
  api.get('/career/website-applicants', { params }).then(r => r.data);
export const importWebsiteApplicant = (data: { email?: string; name?: string; phone?: string; position_id?: string; external_id?: string }) =>
  api.post('/career/website-applicants/import', data).then(r => r.data);

export default api;
